'use client';
import React, { useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { BaseTooltip } from '@/components';
import type { HarmonicSelection } from '../../types';

interface HarmonicsPopoverProps {
  selections: HarmonicSelection[];
  onAddSelection: (selection: HarmonicSelection) => void;
  onUpdateSelection: (id: string, updates: Partial<HarmonicSelection>) => void;
  onRemoveSelection: (id: string) => void;
  onRemoveAll: () => void;
  onFocus: (id: string) => void;
  onReset: (id: string) => void;
  onEnterSelectionMode: () => void;
  onGenerateHarmonics: (
    startingPoint: number,
    multiplier: number,
    label: string,
    intervalType?: 'seconds' | 'minutes' | 'hours' | 'days',
    xAxisType?: 'date' | 'numeric' | 'category',
    chartId?: string
  ) => void;
  isSelectionMode?: boolean;
  disabled?: boolean;
  xAxisType?: 'date' | 'numeric' | 'category';
}

export const HarmonicsPopover: React.FC<HarmonicsPopoverProps> = ({
  selections,
  onUpdateSelection,
  onRemoveSelection,
  onRemoveAll,
  onFocus,
  onReset,
  onEnterSelectionMode,
  onGenerateHarmonics,
  isSelectionMode = false,
  disabled = false,
  xAxisType = 'numeric',
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Local state for starting point configuration
  const [startingPoint, setStartingPoint] = useState<number | null>(null);
  const [multiplier, setMultiplier] = useState(6);
  const [labelPrefix, setLabelPrefix] = useState('H');
  const [intervalType, setIntervalType] = useState<'seconds' | 'minutes' | 'hours' | 'days'>('hours');

  // Check if we have a starting point captured (first selection)
  const hasStartingPoint = selections.length > 0;
  const capturedStartingPoint = hasStartingPoint ? selections[0] : null;

  // Use the detected xAxisType from the selection, fallback to prop
  const actualXAxisType = capturedStartingPoint?.xAxisType || xAxisType;

  // Debug logging

  // Determine the x-axis label based on type
  const xAxisLabel = actualXAxisType === 'date' ? 'Date' : 'Hz';

  return (
    <>
      {/* Harmonics mode toggle button */}
      <BaseTooltip content="Add evenly spaced vertical lines (harmonics) - click a starting point, spacing will be relative to zero">
        <Button
          ref={buttonRef}
          variant="ghost"
          size="sm"
          className={`gap-2 ${isSelectionMode ? 'bg-secondary text-primary-500 font-semibold' : 'text-[#55687d]'}`}
          disabled={disabled}
          onClick={() => {
            onEnterSelectionMode();
            // Log after a brief delay to confirm state change
            setTimeout(() => {}, 100);
          }}
          style={isSelectionMode ? { cursor: 'crosshair' } : {}}
        >
          <Plus className="w-4 h-4" />
          Harmonics
        </Button>
      </BaseTooltip>

      {/* Global cursor change when in selection mode */}
      {isSelectionMode && (
        <style>{`
          body { cursor: crosshair !important; }
          * { cursor: crosshair !important; }
        `}</style>
      )}
    </>
  );
};
